import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Heart, Loader2, Mail, MessageSquare, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SponsorPage = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: "Sponsorship Inquiry",
        message: `Organization: ${form.organization || "Not provided"}\n\n${form.message}`,
      });
      setSubmitted(true);
      toast.success("Sponsorship inquiry sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-fade-in">
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-20 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#42D78A]/10 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-[#42D78A]" />
          </div>
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Sponsor Us</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Help Build a Stronger Local Economy
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Sponsorship helps 614 Shop Local keep expanding tools, visibility, and community resources for local businesses, markets, and shoppers.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-12 items-start">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Sponsorship can support
            </h2>
            <div className="space-y-4 text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {["Directory growth", "Community events", "Local business resources", "Market and vendor visibility"].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#42D78A]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-[#42D78A]/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-[#42D78A]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Inquiry Sent
                </h2>
                <p className="text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Thanks for reaching out. We&apos;ll follow up with you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="sponsor-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Name *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="Your name" className="h-12 pl-10 rounded-lg border-gray-200" disabled={loading} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="you@email.com" className="h-12 pl-10 rounded-lg border-gray-200" disabled={loading} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Organization</Label>
                  <Input value={form.organization} onChange={(e) => handleChange("organization", e.target.value)} placeholder="Business or organization name" className="h-12 mt-1 rounded-lg border-gray-200" disabled={loading} />
                </div>
                <div>
                  <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Message *</Label>
                  <div className="relative mt-1">
                    <MessageSquare className="absolute left-3 top-4 w-4 h-4 text-gray-400" />
                    <Textarea value={form.message} onChange={(e) => handleChange("message", e.target.value)} placeholder="Tell us about your sponsorship interest..." className="min-h-[150px] pl-10 rounded-lg border-gray-200" disabled={loading} />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full py-6 text-base font-bold transition-all hover:-translate-y-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Sponsorship Inquiry"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SponsorPage;
